import React, { memo, useMemo, useCallback } from 'react';

interface PowerFlowDiagramProps {
  solarPower: number;
  loadPower: number;
  gridPower: number;
  inverterCount: number;
  hybridCount: number;
  hybridSolarPower: number;
  hybridBatteryPower: number;
  hybridBatterySoc: number;
}

export const PowerFlowDiagram = memo(function PowerFlowDiagram({
  solarPower,
  loadPower,
  gridPower,
  inverterCount,
  hybridCount,
  hybridSolarPower,
  hybridBatteryPower,
  hybridBatterySoc,
}: PowerFlowDiagramProps) {
  const formatPower = useCallback((watts: number) => {
    const absWatts = Math.abs(watts);
    if (absWatts >= 1000) return `${(watts / 1000).toFixed(1)}kW`;
    return `${Math.round(watts)}W`;
  }, []);

  // Determine flow states
  const solarFlowing = solarPower > 50;
  const hybridSolarFlowing = hybridSolarPower > 50;
  const gridExporting = gridPower > 50;
  const gridImporting = gridPower < -50;
  const batteryCharging = hybridBatteryPower < -50;
  const batteryDischarging = hybridBatteryPower > 50;
  const hasHybrids = hybridCount > 0;
  const hasInverters = inverterCount > 0;
  const hasBothTypes = hasHybrids && hasInverters;

  // Element heights for centering calculations
  const SOLAR_HEIGHT = 60;
  const INVERTER_HEIGHT = 60;
  const HOUSE_HEIGHT = 80;
  const PYLON_HEIGHT = 90;

  // Dynamic Y positions - aligned by vertical center
  // All elements in the same row have their centers at the same Y
  const getYPositions = () => {
    if (hasBothTypes) {
      // Two rows: PV inverters at center=60, Hybrids at center=160
      const row1Center = 60;
      const row2Center = 160;
      const mainRowCenter = 110; // House/grid centered between the two rows
      return {
        inverterY: row1Center - INVERTER_HEIGHT / 2,       // 30
        solarY: row1Center - SOLAR_HEIGHT / 2,             // 30
        hybridY: row2Center - INVERTER_HEIGHT / 2,         // 130
        hybridSolarY: row2Center - SOLAR_HEIGHT / 2,       // 130
        loadY: mainRowCenter - HOUSE_HEIGHT / 2,           // 70
        gridY: mainRowCenter - PYLON_HEIGHT / 2,           // 65
        batteryY: row2Center + INVERTER_HEIGHT / 2 + 15,   // 205
        row1Center,
        row2Center,
        mainRowCenter,
      };
    } else if (hasInverters) {
      // Single row: center at y=80
      const rowCenter = 80;
      return {
        inverterY: rowCenter - INVERTER_HEIGHT / 2,  // 50
        solarY: rowCenter - SOLAR_HEIGHT / 2,        // 50
        hybridY: 0,
        hybridSolarY: 0,
        loadY: rowCenter - HOUSE_HEIGHT / 2,         // 40
        gridY: rowCenter - PYLON_HEIGHT / 2,         // 35
        batteryY: 0,
        row1Center: rowCenter,
        row2Center: 0,
        mainRowCenter: rowCenter,
      };
    } else if (hasHybrids) {
      // Single row for hybrids at center=80, battery below
      const rowCenter = 80;
      return {
        inverterY: 0,
        solarY: 0,
        hybridY: rowCenter - INVERTER_HEIGHT / 2,     // 50
        hybridSolarY: rowCenter - SOLAR_HEIGHT / 2,   // 50
        loadY: rowCenter - HOUSE_HEIGHT / 2,          // 40
        gridY: rowCenter - PYLON_HEIGHT / 2,          // 35
        batteryY: rowCenter + INVERTER_HEIGHT / 2 + 15, // 125
        row1Center: 0,
        row2Center: rowCenter,
        mainRowCenter: rowCenter,
      };
    }
    // Default fallback
    const rowCenter = 80;
    return {
      inverterY: rowCenter - INVERTER_HEIGHT / 2,
      solarY: rowCenter - SOLAR_HEIGHT / 2,
      hybridY: rowCenter - INVERTER_HEIGHT / 2,
      hybridSolarY: rowCenter - SOLAR_HEIGHT / 2,
      loadY: rowCenter - HOUSE_HEIGHT / 2,
      gridY: rowCenter - PYLON_HEIGHT / 2,
      batteryY: rowCenter + INVERTER_HEIGHT / 2 + 15,
      row1Center: rowCenter,
      row2Center: rowCenter,
      mainRowCenter: rowCenter,
    };
  };

  const pos = getYPositions();
  const svgHeight = hasBothTypes ? 270 : 200;

  // SVG Icons as components
  const SolarPanelIcon = ({ x, y, color }: { x: number; y: number; color: string }) => (
    <g transform={`translate(${x}, ${y})`}>
      {/* Panel frame */}
      <rect x="0" y="0" width="90" height="60" rx="4" fill="#1e293b" stroke={color} strokeWidth="2"/>
      {/* Panel cells - 3x2 grid */}
      <rect x="5" y="5" width="25" height="23" rx="2" fill={color} opacity="0.3"/>
      <rect x="33" y="5" width="25" height="23" rx="2" fill={color} opacity="0.3"/>
      <rect x="61" y="5" width="24" height="23" rx="2" fill={color} opacity="0.3"/>
      <rect x="5" y="32" width="25" height="23" rx="2" fill={color} opacity="0.3"/>
      <rect x="33" y="32" width="25" height="23" rx="2" fill={color} opacity="0.3"/>
      <rect x="61" y="32" width="24" height="23" rx="2" fill={color} opacity="0.3"/>
      {/* Grid lines */}
      <line x1="30" y1="5" x2="30" y2="55" stroke={color} strokeWidth="1" opacity="0.5"/>
      <line x1="60" y1="5" x2="60" y2="55" stroke={color} strokeWidth="1" opacity="0.5"/>
      <line x1="5" y1="30" x2="85" y2="30" stroke={color} strokeWidth="1" opacity="0.5"/>
    </g>
  );

  const InverterIcon = ({ x, y, color, count }: { x: number; y: number; color: string; count: number }) => (
    <g transform={`translate(${x}, ${y})`}>
      {/* Inverter box */}
      <rect x="0" y="0" width="80" height="60" rx="4" fill="#1e293b" stroke={color} strokeWidth="2"/>
      {/* Heat sink lines */}
      <line x1="8" y1="5" x2="8" y2="55" stroke={color} strokeWidth="2" opacity="0.4"/>
      <line x1="16" y1="5" x2="16" y2="55" stroke={color} strokeWidth="2" opacity="0.4"/>
      <line x1="24" y1="5" x2="24" y2="55" stroke={color} strokeWidth="2" opacity="0.4"/>
      {/* Display area */}
      <rect x="32" y="8" width="40" height="20" rx="2" fill="#0f172a" stroke={color} strokeWidth="1" opacity="0.6"/>
      {/* Count display */}
      <text x="52" y="23" textAnchor="middle" fill={color} fontSize="12" fontWeight="bold">{count}</text>
      {/* Status LEDs */}
      <circle cx="40" cy="42" r="4" fill="#22c55e"/>
      <circle cx="52" cy="42" r="4" fill={color} opacity="0.5"/>
      <circle cx="64" cy="42" r="4" fill={color} opacity="0.3"/>
    </g>
  );

  const HouseIcon = ({ x, y }: { x: number; y: number }) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="0" y="0" width="100" height="80" rx="4" fill="#1e293b" stroke="#f97316" strokeWidth="2"/>
      {/* Roof */}
      <polygon points="50,8 15,35 85,35" fill="#f97316" opacity="0.3" stroke="#f97316" strokeWidth="1.5"/>
      {/* House body */}
      <rect x="20" y="35" width="60" height="35" fill="#f97316" opacity="0.15"/>
      {/* Door */}
      <rect x="42" y="48" width="16" height="22" rx="2" fill="#f97316" opacity="0.4"/>
      {/* Windows */}
      <rect x="25" y="42" width="12" height="10" rx="1" fill="#fbbf24" opacity="0.5"/>
      <rect x="63" y="42" width="12" height="10" rx="1" fill="#fbbf24" opacity="0.5"/>
    </g>
  );

  const GridPylonIcon = ({ x, y, isExporting, isImporting }: { x: number; y: number; isExporting: boolean; isImporting: boolean }) => {
    const color = isExporting ? "#22c55e" : isImporting ? "#ef4444" : "#64748b";
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x="0" y="0" width="100" height="90" rx="4" fill="#1e293b" stroke={color} strokeWidth="2"/>
        {/* Pylon structure */}
        <polygon points="50,10 30,75 35,75 50,25 65,75 70,75" fill="none" stroke={color} strokeWidth="2"/>
        {/* Cross beams */}
        <line x1="35" y1="40" x2="65" y2="40" stroke={color} strokeWidth="1.5"/>
        <line x1="37" y1="55" x2="63" y2="55" stroke={color} strokeWidth="1.5"/>
        {/* Power lines */}
        <line x1="10" y1="30" x2="90" y2="30" stroke={color} strokeWidth="1.5"/>
        <line x1="15" y1="35" x2="85" y2="35" stroke={color} strokeWidth="1"/>
        {/* Insulators */}
        <circle cx="30" cy="30" r="3" fill={color}/>
        <circle cx="50" cy="30" r="3" fill={color}/>
        <circle cx="70" cy="30" r="3" fill={color}/>
      </g>
    );
  };

  const BatteryIcon = ({ x, y, soc, power, charging, discharging }: { x: number; y: number; soc: number; power: number; charging: boolean; discharging: boolean }) => {
    const color = charging ? "#22c55e" : discharging ? "#ef4444" : "#64748b";
    return (
      <g transform={`translate(${x}, ${y})`}>
        {/* Battery body */}
        <rect x="0" y="0" width="80" height="35" rx="4" fill="#1e293b" stroke={color} strokeWidth="2"/>
        {/* Battery terminal */}
        <rect x="80" y="10" width="6" height="15" rx="2" fill={color}/>
        {/* Fill level */}
        <rect x="4" y="4" width={Math.max(0, Math.min(72, 72 * soc / 100))} height="27" rx="2" fill={color} opacity="0.4"/>
        {/* Percentage and power text */}
        <text x="40" y="15" textAnchor="middle" fill="#f8fafc" fontSize="10" fontWeight="bold">
          {Math.round(soc)}%
        </text>
        <text x="40" y="28" textAnchor="middle" fill={color} fontSize="9">
          {formatPower(Math.abs(power))}
        </text>
      </g>
    );
  };

  const AnimatedArrow = ({ x1, y1, x2, y2, color, active, reverse }: { x1: number; y1: number; x2: number; y2: number; color: string; active: boolean; reverse?: boolean }) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const arrowSize = 8;
    const endX = reverse ? x1 : x2;
    const endY = reverse ? y1 : y2;

    return (
      <g>
        <line
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={active ? color : "#475569"}
          strokeWidth="3"
          strokeDasharray="8,4"
          className={active ? (reverse ? "power-flow-line flowing-left" : "power-flow-line flowing-right") : ""}
        />
        <polygon
          points={`
            ${endX},${endY}
            ${endX - arrowSize * Math.cos(angle - Math.PI / 6)},${endY - arrowSize * Math.sin(angle - Math.PI / 6)}
            ${endX - arrowSize * Math.cos(angle + Math.PI / 6)},${endY - arrowSize * Math.sin(angle + Math.PI / 6)}
          `}
          fill={active ? color : "#475569"}
        />
      </g>
    );
  };

  return (
    <div className="dashboard-card p-4 sm:p-6">
      <h3 className="text-xs sm:text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 sm:mb-4">
        Power Flow
      </h3>

      <div className="relative overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ minHeight: svgHeight }}>
        <svg viewBox={`0 0 650 ${svgHeight}`} className="w-full h-full min-w-[500px] sm:min-w-0">

          {/* ============ PV INVERTERS ROW ============ */}
          {hasInverters && (
            <>
              <SolarPanelIcon x={20} y={pos.solarY} color="#fbbf24" />
              <text x={65} y={pos.solarY + SOLAR_HEIGHT + 15} textAnchor="middle" fill="#f8fafc" fontSize="12" fontWeight="bold">
                {formatPower(solarPower)}
              </text>

              <AnimatedArrow x1={120} y1={pos.row1Center} x2={170} y2={pos.row1Center} color="#fbbf24" active={solarFlowing} />

              <InverterIcon x={180} y={pos.inverterY} color="#3b82f6" count={inverterCount} />
              <text x={220} y={pos.inverterY + INVERTER_HEIGHT + 12} textAnchor="middle" fill="#94a3b8" fontSize="9">INVERTERS</text>

              <AnimatedArrow
                x1={270} y1={pos.row1Center}
                x2={340} y2={pos.mainRowCenter}
                color="#f97316"
                active={solarFlowing}
              />
            </>
          )}

          {/* ============ HYBRID ROW ============ */}
          {hasHybrids && (
            <>
              <SolarPanelIcon x={20} y={pos.hybridSolarY} color="#a855f7" />
              <text x={65} y={pos.hybridSolarY + SOLAR_HEIGHT + 15} textAnchor="middle" fill="#f8fafc" fontSize="12" fontWeight="bold">
                {formatPower(hybridSolarPower)}
              </text>

              <AnimatedArrow x1={120} y1={pos.row2Center || pos.mainRowCenter} x2={170} y2={pos.row2Center || pos.mainRowCenter} color="#a855f7" active={hybridSolarFlowing} />

              <InverterIcon x={180} y={pos.hybridY} color="#a855f7" count={hybridCount} />
              <text x={220} y={pos.hybridY + INVERTER_HEIGHT + 12} textAnchor="middle" fill="#94a3b8" fontSize="9">HYBRIDS</text>

              <AnimatedArrow
                x1={270} y1={pos.row2Center || pos.mainRowCenter}
                x2={340} y2={pos.mainRowCenter}
                color="#f97316"
                active={hybridSolarFlowing || batteryDischarging}
              />

              {/* Battery */}
              <BatteryIcon
                x={180}
                y={pos.batteryY}
                soc={hybridBatterySoc}
                power={hybridBatteryPower}
                charging={batteryCharging}
                discharging={batteryDischarging}
              />

              {/* Battery connection */}
              <g>
                {batteryCharging && (
                  <AnimatedArrow x1={220} y1={pos.hybridY + INVERTER_HEIGHT} x2={220} y2={pos.batteryY} color="#22c55e" active={true} />
                )}
                {batteryDischarging && (
                  <AnimatedArrow x1={220} y1={pos.batteryY} x2={220} y2={pos.hybridY + INVERTER_HEIGHT} color="#ef4444" active={true} />
                )}
                {!batteryCharging && !batteryDischarging && (
                  <line x1={220} y1={pos.hybridY + INVERTER_HEIGHT} x2={220} y2={pos.batteryY} stroke="#475569" strokeWidth="2"/>
                )}
              </g>
            </>
          )}

          {/* ============ LOAD (HOUSE) ============ */}
          <HouseIcon x={340} y={pos.loadY} />
          <text x={390} y={pos.loadY + HOUSE_HEIGHT + 15} textAnchor="middle" fill="#f8fafc" fontSize="12" fontWeight="bold">
            {formatPower(loadPower)}
          </text>

          {/* ============ LOAD TO GRID ARROW ============ */}
          {gridExporting ? (
            <AnimatedArrow x1={440} y1={pos.mainRowCenter} x2={510} y2={pos.mainRowCenter} color="#22c55e" active={true} />
          ) : gridImporting ? (
            <AnimatedArrow x1={510} y1={pos.mainRowCenter} x2={440} y2={pos.mainRowCenter} color="#ef4444" active={true} reverse />
          ) : (
            <line x1={440} y1={pos.mainRowCenter} x2={510} y2={pos.mainRowCenter} stroke="#475569" strokeWidth="2"/>
          )}

          {/* ============ GRID (PYLON) ============ */}
          <GridPylonIcon x={510} y={pos.gridY} isExporting={gridExporting} isImporting={gridImporting} />
          <text x={560} y={pos.gridY + PYLON_HEIGHT + 15} textAnchor="middle" fill="#f8fafc" fontSize="12" fontWeight="bold">
            {formatPower(Math.abs(gridPower))}
          </text>
          <text x={560} y={pos.gridY + PYLON_HEIGHT + 28} textAnchor="middle" fill={gridExporting ? "#22c55e" : gridImporting ? "#ef4444" : "#64748b"} fontSize="10" fontWeight="medium">
            {gridExporting ? 'EXPORTING' : gridImporting ? 'IMPORTING' : 'BALANCED'}
          </text>

        </svg>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mt-3 sm:mt-4 text-xs text-slate-400">
        {hasInverters && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-amber-400"></div>
            <span>PV Solar</span>
          </div>
        )}
        {hasHybrids && (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-purple-500"></div>
              <span>Hybrid</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-green-500"></div>
              <span>Charging</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span>Discharging</span>
            </div>
          </>
        )}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span>Export</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-500"></div>
          <span>Import</span>
        </div>
      </div>
    </div>
  );
});

export default PowerFlowDiagram;
